import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Inbox, PackageCheck, Truck, type LucideIcon } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, OnlineOrder, PagedResponse } from '@/services/types';
import { API_ENDPOINTS, DEBOUNCE_DELAY } from '@/utils/constants';
import { Alert, Button, Chip, LoadingSkeleton, SearchPill } from '@/components/ui';
import { StoreHeader } from '@/components/layout/StoreHeader';
import { onPush } from '@/features/notifications/push';
import { useIsLandscape } from '@/hooks/useMediaQuery';
import { OnlineOrderDetail } from './OnlineOrderDetail';
import { BarcodeScannerModal, hasBarcodeDetector } from '@/features/catalog/BarcodeScannerModal';
import { OrderCard } from './OrderCard';
import { OrderActionSheet, useOnlineOrderActions } from './useOnlineOrderActions';
import { useOnlineOrderSummary } from './useOnlineOrderSummary';

const TABS = {
    NEEDS_PROCESS: ['PAID'],
    READY: ['READY'],
    SENT: ['SENT'],
    HISTORY: ['DELIVERED', 'DONE'],
} as const;
type Tab = keyof typeof TABS;
const PAGE_SIZE = 20;
const BOARD: { tab: Tab; icon: LucideIcon }[] = [{ tab: 'NEEDS_PROCESS', icon: Inbox }, { tab: 'READY', icon: PackageCheck }, { tab: 'SENT', icon: Truck }];

/** One status bucket: fetch + list. Used once on phone (active tab) and three times on the landscape board. */
function useOrders(shopId: number, statuses: readonly string[], query: string, refreshKey: number) {
    const [orders, setOrders] = useState<OnlineOrder[]>([]);
    const [page, setPage] = useState(0);
    const [last, setLast] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (p: number, replace: boolean) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: String(p), size: String(PAGE_SIZE) });
            if (query) params.set('q', query);
            statuses.forEach((s) => params.append('status', s));
            const list = await api.get<ApiResponse<PagedResponse<OnlineOrder>>>(`${API_ENDPOINTS.ONLINE_ORDERS(shopId)}?${params}`);
            const content = list.data.data.content || [];
            setOrders((prev) => (replace ? content : [...prev, ...content]));
            setLast(list.data.data.page?.last ?? content.length < PAGE_SIZE);
            setPage(p);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [shopId, statuses, query]);

    useEffect(() => { load(0, true); }, [load, refreshKey]);
    return { orders, loading, error, last, loadMore: () => load(page + 1, false) };
}

const OrderList: React.FC<{ shopId: number; statuses: readonly string[]; query: string; refreshKey: number; actions: ReturnType<typeof useOnlineOrderActions>; onOpen: (id: string) => void; className?: string }> =
    ({ shopId, statuses, query, refreshKey, actions, onOpen, className }) => {
        const { t } = useTranslation();
        const { orders, loading, error, last, loadMore } = useOrders(shopId, statuses, query, refreshKey);
        return (
            <div className={clsx('flex flex-col gap-3', className)}>
                {error && <Alert variant="error" message={error} />}
                {loading && orders.length === 0 && [1, 2].map((i) => <LoadingSkeleton key={i} height={120} />)}
                {!loading && orders.length === 0 && <p className="py-8 text-center text-sm text-ink-muted">{t('onlineOrders.empty')}</p>}
                {orders.map((o) => <OrderCard key={o.id} order={o} actions={actions} onOpen={() => onOpen(o.transactionId)} />)}
                {!last && <Button variant="outline" size="sm" onClick={loadMore} loading={loading} className="self-center">{t('common.loadMore')}</Button>}
            </div>
        );
    };

/** 12 Pesanan online. Phone: chips + cards. Landscape: 3-column board (Perlu diproses · Siap · Dikirim) or history list. */
const OnlineOrdersPage: React.FC = () => {
    const { t } = useTranslation();
    const isLandscape = useIsLandscape();
    const { selectedShop } = useAppSelector((s) => s.auth);
    const summary = useOnlineOrderSummary();
    const [tab, setTab] = useState<Tab>('NEEDS_PROCESS');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<string | null>(null);
    const [scan, setScan] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
    const actions = useOnlineOrderActions(selectedShop?.shopId ?? 0, bump);

    useEffect(() => {
        const id = setTimeout(() => setQuery(search.trim()), DEBOUNCE_DELAY);
        return () => clearTimeout(id);
    }, [search]);
    useEffect(() => onPush(bump), [bump]);

    if (!selectedShop) return null;
    const shopId = selectedShop.shopId;
    const counts: Partial<Record<Tab, number>> = { NEEDS_PROCESS: summary?.newPaidCount, READY: summary?.readyCount, SENT: summary?.sentCount };
    const chipLabel = (k: Tab) => `${t(`onlineOrders.tabs.${k}` as 'onlineOrders.tabs.ALL')}${counts[k] ? ` · ${counts[k]}` : ''}`;

    if (selected) {
        return <OnlineOrderDetail shopId={shopId} transactionId={selected} onClose={() => setSelected(null)} onChanged={bump} />;
    }

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col px-5 pt-2.5 lg:px-6 lg:pt-[22px]">
            <StoreHeader className="lg:hidden" />
            <div className="mt-2 flex items-center gap-3 lg:mt-0">
                <h1 className="flex-1 text-2xl font-black lg:text-[26px]">{t('onlineOrders.title')}</h1>
                <SearchPill size="md" value={search} onChange={setSearch} placeholder={t('onlineOrders.search')} className="hidden !flex-none lg:flex lg:w-[320px]" />
                <Chip selected={tab === 'HISTORY'} onClick={() => setTab(tab === 'HISTORY' ? 'NEEDS_PROCESS' : 'HISTORY')} className="hidden lg:inline-flex">{t('onlineOrders.history')}</Chip>
            </div>
            <SearchPill size="md" value={search} onChange={setSearch} placeholder={t('onlineOrders.search')} className="mt-3 !flex-none lg:hidden" />
            <div className="no-scrollbar -mx-5 mt-3 flex gap-2 overflow-x-auto px-5 lg:hidden">
                {(Object.keys(TABS) as Tab[]).map((k) => <Chip key={k} selected={tab === k} onClick={() => setTab(k)}>{chipLabel(k)}</Chip>)}
            </div>

            <div className="mt-3.5 min-h-0 flex-1 overflow-y-auto pb-6 lg:mt-[18px] lg:overflow-hidden">
                {!isLandscape || tab === 'HISTORY' ? (
                    <OrderList shopId={shopId} statuses={TABS[tab]} query={query} refreshKey={refreshKey} actions={actions} onOpen={setSelected} className="lg:h-full lg:overflow-y-auto" />
                ) : (
                    <div className="grid h-full grid-cols-3 gap-4">
                        {BOARD.map(({ tab: k, icon: Icon }) => (
                            <div key={k} className="flex min-h-0 flex-col gap-3 overflow-hidden rounded-xl bg-surface-sunken p-3.5">
                                <div className="flex items-center gap-2 px-1">
                                    <Icon className="h-[17px] w-[17px] text-ink-brand" />
                                    <span className="flex-1 text-[15px] font-black">{t(`onlineOrders.tabs.${k}` as 'onlineOrders.tabs.ALL')}</span>
                                    <span className="flex h-6 min-w-[24px] items-center justify-center rounded-pill bg-surface-card px-1.5 text-xs font-black tnum">{counts[k] ?? 0}</span>
                                </div>
                                <OrderList shopId={shopId} statuses={TABS[k]} query={query} refreshKey={refreshKey} actions={actions} onOpen={setSelected} className="min-h-0 flex-1 overflow-y-auto" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <OrderActionSheet actions={actions} onScan={hasBarcodeDetector() ? () => setScan(true) : undefined} />
            <BarcodeScannerModal isOpen={scan} continuous={false} onClose={() => setScan(false)} onDetected={(code) => { actions.setAirwaybill(code); setScan(false); }} />
        </div>
    );
};

export default OnlineOrdersPage;
